<template>
    <div ref="root" class="presentation-player">

        <!-- Default slot: video -->
        <slot />

        <!-- Hidden media slot – queried once in mounted() for pre-rendered images -->
        <div ref="mediaContainer" hidden>
            <slot name="media" />
        </div>

        <!-- Settings popover – playback controls, toggled from the nav (positioned above it) -->
        <div
            v-show="settingsOpen"
            class="presentation-player__settings-bar"
            role="group"
            :aria-label="labels.controls"
        >
            <button
                class="presentation-player__nav-btn"
                :aria-label="isPlaying ? labels.pause : labels.play"
                :title="isPlaying ? labels.pause : labels.play"
                @click="togglePlay"
            >
                <!-- Pause -->
                <svg v-if="isPlaying" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M20.25 4.5V19.5C20.25 19.8978 20.092 20.2794 19.8107 20.5607C19.5294 20.842 19.1478 21 18.75 21H15C14.6022 21 14.2206 20.842 13.9393 20.5607C13.658 20.2794 13.5 19.8978 13.5 19.5V4.5C13.5 4.10218 13.658 3.72064 13.9393 3.43934C14.2206 3.15804 14.6022 3 15 3H18.75C19.1478 3 19.5294 3.15804 19.8107 3.43934C20.092 3.72064 20.25 4.10218 20.25 4.5ZM9 3H5.25C4.85218 3 4.47064 3.15804 4.18934 3.43934C3.90804 3.72064 3.75 4.10218 3.75 4.5V19.5C3.75 19.8978 3.90804 20.2794 4.18934 20.5607C4.47064 20.842 4.85218 21 5.25 21H9C9.39782 21 9.77936 20.842 10.0607 20.5607C10.342 20.2794 10.5 19.8978 10.5 19.5V4.5C10.5 4.10218 10.342 3.72064 10.0607 3.43934C9.77936 3.15804 9.39782 3 9 3Z" fill="currentColor"/>
                </svg>
                <!-- Play -->
                <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M22.5 12C22.5006 12.2546 22.4353 12.5051 22.3105 12.727C22.1856 12.949 22.0055 13.1348 21.7875 13.2665L8.28 21.5297C8.05227 21.6691 7.79144 21.7452 7.52445 21.7502C7.25746 21.7551 6.99399 21.6887 6.76125 21.5578C6.53073 21.4289 6.3387 21.2409 6.2049 21.0132C6.07111 20.7855 6.00039 20.5263 6 20.2622V3.73779C6.00039 3.47368 6.07111 3.21445 6.2049 2.98673C6.3387 2.75902 6.53073 2.57106 6.76125 2.44217C6.99399 2.31124 7.25746 2.24482 7.52445 2.24977C7.79144 2.25471 8.05227 2.33084 8.28 2.47029L21.7875 10.7334C22.0055 10.8651 22.1856 11.051 22.3105 11.2729C22.4353 11.4949 22.5006 11.7453 22.5 12Z" fill="currentColor"/>
                </svg>
            </button>

            <button
                class="presentation-player__nav-btn"
                :class="{ 'presentation-player__nav-btn--active': autoplay }"
                :aria-label="labels.autoplay"
                :title="labels.autoplay"
                @click="autoplay = !autoplay"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M21 12C21.0002 14.3662 20.0686 16.6372 18.4069 18.3216C16.7451 20.006 14.4869 20.9682 12.1209 21H12C9.70149 21.0057 7.48903 20.1261 5.82189 18.5438C5.7503 18.476 5.69274 18.3949 5.65251 18.3049C5.61229 18.215 5.59017 18.118 5.58743 18.0195C5.58468 17.921 5.60137 17.8229 5.63653 17.7309C5.67169 17.6388 5.72464 17.5546 5.79236 17.483C5.86007 17.4114 5.94122 17.3538 6.03117 17.3136C6.12113 17.2734 6.21813 17.2513 6.31663 17.2485C6.41513 17.2458 6.51321 17.2625 6.60527 17.2976C6.69732 17.3328 6.78155 17.3857 6.85314 17.4534C7.92545 18.4647 9.27197 19.1376 10.7245 19.3881C12.177 19.6385 13.6711 19.4554 15.0202 18.8617C16.3692 18.2679 17.5134 17.2898 18.3098 16.0495C19.1062 14.8092 19.5195 13.3618 19.4981 11.888C19.4766 10.4142 19.0214 8.97943 18.1893 7.76285C17.3571 6.54628 16.1849 5.60187 14.8192 5.04761C13.4534 4.49334 11.9546 4.3538 10.51 4.6464C9.06538 4.939 7.73901 5.65078 6.69658 6.69282C6.68892 6.70111 6.68078 6.70893 6.6722 6.71626L5.68408 7.62095L7.27782 9.2147C7.38428 9.31917 7.45718 9.453 7.48719 9.59911C7.5172 9.74521 7.50297 9.89694 7.44631 10.0349C7.38965 10.1729 7.29314 10.2908 7.16911 10.3737C7.04508 10.4565 6.89917 10.5005 6.75001 10.5H2.25001C2.0511 10.5 1.86033 10.421 1.71968 10.2803C1.57903 10.1397 1.50001 9.94892 1.50001 9.75001V5.25001C1.49915 5.10127 1.54253 4.95563 1.62465 4.83161C1.70677 4.70759 1.82391 4.61078 1.96118 4.5535C2.09845 4.49622 2.24966 4.48105 2.39557 4.50992C2.54149 4.53879 2.67552 4.6104 2.78064 4.71564L4.62283 6.56251L5.64845 5.62501C6.90843 4.36992 8.51201 3.51626 10.2568 3.17179C12.0015 2.82732 13.8092 3.00748 15.4516 3.68953C17.0941 4.37158 18.4976 5.52495 19.4851 7.00402C20.4726 8.4831 20.9997 10.2216 21 12Z" fill="currentColor"/>
                </svg>
            </button>

            <button
                class="presentation-player__nav-btn"
                :class="{ 'presentation-player__nav-btn--active': hotspotEnabled }"
                :aria-label="labels.hotspotToggle"
                :title="labels.hotspotToggle"
                @click="hotspotEnabled = !hotspotEnabled"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M15.75 12C15.75 12.7417 15.5301 13.4667 15.118 14.0834C14.7059 14.7001 14.1203 15.1807 13.4351 15.4646C12.7498 15.7484 11.9958 15.8227 11.2684 15.678C10.541 15.5333 9.87279 15.1761 9.34834 14.6517C8.82389 14.1272 8.46674 13.459 8.32204 12.7316C8.17735 12.0042 8.25161 11.2502 8.53544 10.5649C8.81927 9.87973 9.29992 9.29406 9.9166 8.882C10.5333 8.46995 11.2583 8.25001 12 8.25001C12.9946 8.25001 13.9484 8.6451 14.6516 9.34836C15.3549 10.0516 15.75 11.0054 15.75 12ZM19.5 12C19.5022 10.1549 18.8221 8.3741 17.5903 7.00032C17.5251 6.92514 17.4457 6.86368 17.3565 6.81952C17.2673 6.77536 17.1703 6.74938 17.071 6.74309C16.9717 6.73681 16.8721 6.75034 16.7781 6.78291C16.6841 6.81547 16.5975 6.86642 16.5234 6.93278C16.4492 6.99913 16.3891 7.07958 16.3463 7.16943C16.3036 7.25928 16.2791 7.35673 16.2744 7.45611C16.2697 7.5555 16.2848 7.65482 16.3189 7.74831C16.353 7.84179 16.4053 7.92757 16.4728 8.00064C17.4566 9.10066 18.0005 10.5247 18.0005 12.0005C18.0005 13.4763 17.4566 14.9003 16.4728 16.0003C16.3439 16.1491 16.2786 16.3425 16.291 16.539C16.3034 16.7354 16.3926 16.9191 16.5393 17.0504C16.686 17.1817 16.8784 17.2501 17.075 17.2408C17.2716 17.2314 17.4567 17.1452 17.5903 17.0006C18.8219 15.6264 19.502 13.8454 19.5 12ZM7.52718 8.00064C7.59471 7.92757 7.64702 7.84179 7.68108 7.74831C7.71513 7.65482 7.73025 7.5555 7.72554 7.45611C7.72083 7.35673 7.6964 7.25928 7.65366 7.16943C7.61092 7.07958 7.55073 6.99913 7.4766 6.93278C7.40247 6.86642 7.31587 6.81547 7.22186 6.78291C7.12784 6.75034 7.02829 6.73681 6.92899 6.74309C6.82969 6.74938 6.73264 6.77536 6.64348 6.81952C6.55433 6.86368 6.47485 6.92514 6.40968 7.00032C5.17855 8.37483 4.49778 10.1552 4.49778 12.0005C4.49778 13.8457 5.17855 15.6261 6.40968 17.0006C6.54328 17.1452 6.72836 17.2314 6.92499 17.2408C7.12162 17.2501 7.31402 17.1817 7.46069 17.0504C7.60736 16.9191 7.69654 16.7354 7.70898 16.539C7.72141 16.3425 7.65612 16.1491 7.52718 16.0003C6.54239 14.9008 5.99785 13.4766 5.99785 12.0005C5.99785 10.5244 6.54239 9.10019 7.52718 8.00064ZM22.3659 7.62564C21.817 6.32208 21.0262 5.13427 20.0353 4.12501C19.967 4.05199 19.8848 3.99336 19.7935 3.95257C19.7022 3.91178 19.6037 3.88966 19.5037 3.8875C19.4038 3.88535 19.3044 3.9032 19.2114 3.94001C19.1185 3.97683 19.0338 4.03186 18.9624 4.10187C18.891 4.17188 18.8344 4.25545 18.7957 4.34768C18.7571 4.43991 18.7373 4.53893 18.7376 4.63892C18.7378 4.7389 18.758 4.83784 18.797 4.9299C18.836 5.02197 18.893 5.1053 18.9647 5.17501C20.7522 6.99623 21.7537 9.44623 21.7537 11.9981C21.7537 14.55 20.7522 17 18.9647 18.8213C18.8955 18.8916 18.8408 18.9748 18.8038 19.0662C18.7668 19.1576 18.7481 19.2555 18.7489 19.3541C18.7497 19.4527 18.7699 19.5502 18.8084 19.6411C18.8468 19.7319 18.9028 19.8143 18.9731 19.8834C19.0434 19.9526 19.1267 20.0073 19.2181 20.0443C19.3095 20.0813 19.4073 20.1 19.506 20.0992C19.6046 20.0984 19.7021 20.0782 19.7929 20.0398C19.8838 20.0013 19.9661 19.9453 20.0353 19.875C21.588 18.2894 22.6376 16.2803 23.0522 14.1001C23.4669 11.92 23.2281 9.66587 22.3659 7.62095V7.62564ZM3.01593 15.795C2.26653 14.0235 2.0584 12.0698 2.41777 10.1802C2.77714 8.29054 3.68793 6.54962 5.0353 5.17689C5.17479 5.03491 5.25216 4.84334 5.25041 4.64432C5.24865 4.4453 5.1679 4.25512 5.02593 4.11564C4.88395 3.97615 4.69238 3.89877 4.49336 3.90053C4.29434 3.90229 4.10416 3.98304 3.96468 4.12501C1.90047 6.22644 0.743896 9.05434 0.743896 12C0.743896 14.9457 1.90047 17.7736 3.96468 19.875C4.03298 19.948 4.11519 20.0067 4.20648 20.0475C4.29777 20.0882 4.39629 20.1104 4.49625 20.1125C4.59621 20.1147 4.6956 20.0968 4.78856 20.06C4.88153 20.0232 4.96619 19.9682 5.03757 19.8982C5.10896 19.8281 5.16563 19.7446 5.20424 19.6523C5.24285 19.5601 5.26264 19.4611 5.26243 19.3611C5.26221 19.2611 5.24201 19.1622 5.20301 19.0701C5.164 18.9781 5.10698 18.8947 5.0353 18.825C4.17719 17.9513 3.49203 16.9233 3.01593 15.795Z" fill="currentColor"/>
                </svg>
            </button>
        </div>

        <!-- Floating chapter navigation – bottom center, stays visible while a chapter panel is open -->
        <nav
            v-if="hotspots.length || slots['technical-data'] || hasVideo"
            v-show="!showIntro"
            class="presentation-player__nav"
            :aria-label="labels.chapterNav"
        >
            <template v-if="hotspots.length">
                <button
                    class="presentation-player__nav-btn"
                    :aria-label="labels.toStart"
                    :title="labels.toStart"
                    @click="jumpToHotspot(hotspots[0])"
                >
                    <!-- Home -->
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                </button>

                <button
                    class="presentation-player__nav-btn"
                    :disabled="!hasPrevChapter"
                    :aria-label="labels.prevChapter"
                    :title="labels.prevChapter"
                    @click="goToPrevChapter"
                >
                    <!-- Chevron left -->
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </button>

                <button
                    class="presentation-player__nav-btn"
                    :class="{ 'presentation-player__nav-btn--active': chaptersOpen }"
                    :aria-label="labels.chapters"
                    :title="labels.chapters"
                    @click="toggleChapters"
                >
                    <!-- List -->
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <line x1="8" y1="6" x2="21" y2="6" />
                        <line x1="8" y1="12" x2="21" y2="12" />
                        <line x1="8" y1="18" x2="21" y2="18" />
                        <line x1="3" y1="6" x2="3.01" y2="6" />
                        <line x1="3" y1="12" x2="3.01" y2="12" />
                        <line x1="3" y1="18" x2="3.01" y2="18" />
                    </svg>
                </button>

                <button
                    class="presentation-player__nav-btn"
                    :disabled="!hasNextChapter"
                    :aria-label="labels.nextChapter"
                    :title="labels.nextChapter"
                    @click="goToNextChapter"
                >
                    <!-- Chevron right -->
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                </button>
            </template>

            <button
                v-if="slots['technical-data']"
                class="presentation-player__nav-btn"
                :class="{ 'presentation-player__nav-btn--active': infoOpen }"
                :aria-label="labels.technicalData"
                :title="labels.technicalData"
                @click="toggleInfo"
            >
                <!-- Info -->
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
            </button>

            <!-- Settings gear – far right, toggles the playback controls popover -->
            <button
                class="presentation-player__nav-btn"
                :class="{ 'presentation-player__nav-btn--active': settingsOpen }"
                :aria-label="labels.settings"
                :title="labels.settings"
                @click="toggleSettings"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
            </button>
        </nav>

        <!-- Chapter list dialog -->
        <transition name="presentation-fade">
            <div
                v-if="chaptersOpen"
                class="presentation-player__dialog"
                @click.self="chaptersOpen = false"
            >
                <div class="presentation-player__dialog-box" role="dialog" :aria-label="labels.chapters">
                    <div class="presentation-player__dialog-header">
                        <p class="presentation-player__dialog-title">{{ labels.chapters }}</p>
                        <button
                            class="presentation-player__dialog-close"
                            :aria-label="labels.close"
                            @click="chaptersOpen = false"
                        >
                            <!-- X -->
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                    <ol class="presentation-player__chapter-list">
                        <li v-for="(hotspot, index) in hotspots" :key="index">
                            <button
                                class="presentation-player__chapter"
                                :class="{ 'presentation-player__chapter--active': currentChapterIndex === index }"
                                @click="jumpToHotspot(hotspot)"
                            >
                                <picture v-if="hotspot.thumbUrl" class="presentation-player__chapter-thumb">
                                    <source
                                        v-if="hotspot.thumbWebpUrl"
                                        :srcset="hotspot.thumbWebpUrl"
                                        type="image/webp"
                                    />
                                    <img :src="hotspot.thumbUrl" alt="" width="200" height="200" />
                                </picture>
                                <span class="presentation-player__chapter-title">{{ hotspot.title }}</span>
                            </button>
                        </li>
                    </ol>
                </div>
            </div>
        </transition>

        <!-- Technical data dialog -->
        <transition name="presentation-fade">
            <div
                v-if="infoOpen"
                class="presentation-player__dialog"
                @click.self="infoOpen = false"
            >
                <div class="presentation-player__dialog-box" role="dialog" :aria-label="labels.technicalData">
                    <div class="presentation-player__dialog-header">
                        <p class="presentation-player__dialog-title">{{ labels.technicalData }}</p>
                        <button
                            class="presentation-player__dialog-close"
                            :aria-label="labels.close"
                            @click="infoOpen = false"
                        >
                            <!-- X -->
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                    <div class="presentation-player__tech-text">
                        <slot name="technical-data" />
                    </div>
                </div>
            </div>
        </transition>

        <!-- Initial overlay: shown before the video starts -->
        <transition name="presentation-fade">
            <div v-if="showIntro" class="presentation-player__overlay">
                <div class="presentation-player__overlay-box presentation-player__overlay-box--intro foreground-dark">
                    <!-- Intro slot rendered directly – no innerHTML reading -->
                    <div v-if="slots.intro" class="presentation-player__intro-text">
                        <slot name="intro" />
                    </div>
                    <button
                        class="presentation-player__start button button--variant-default button--style-solid"
                        @click="startPresentation"
                    >
                        <span class="button__label">{{ labels.start }}</span>
                    </button>
                </div>
            </div>
        </transition>

        <!-- Hotspot panel: slides in from left -->
        <transition name="presentation-slide">
            <div
                v-if="activeHotspot"
                class="presentation-player__hotspot-panel"
                :class="{ 'presentation-player__hotspot-panel--collapsed': panelCollapsed }"
            >
                <!-- Toggle strip – always visible, 42px wide -->
                <button
                    class="presentation-player__hotspot-panel-toggle"
                    :aria-label="panelCollapsed ? labels.panelShow : labels.panelHide"
                    @click="panelCollapsed = !panelCollapsed"
                >
                    <span aria-hidden="true">{{ panelCollapsed ? '›' : '‹' }}</span>
                </button>

                <!-- Inner content – clipped when collapsed -->
                <div class="presentation-player__hotspot-panel-inner">

                    <!-- Active panel media image -->
                    <div
                        v-if="activePanel?.mediaHtml"
                        class="presentation-player__hotspot-panel-image"
                    >
                        <div class="presentation-player__hotspot-panel-image-media" v-html="activePanel.mediaHtml" />
                        <button
                            class="presentation-player__expand-btn"
                            :aria-label="labels.expandImage"
                            @click="expandedMedia = true"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <polyline points="15 3 21 3 21 9" />
                                <polyline points="9 21 3 21 3 15" />
                                <line x1="21" y1="3" x2="14" y2="10" />
                                <line x1="3" y1="21" x2="10" y2="14" />
                            </svg>
                        </button>
                    </div>

                    <!-- Scrollable content: title + accordion + sticky button -->
                    <div class="presentation-player__hotspot-panel-content">
                        <p v-if="activeHotspot.title" class="presentation-player__hotspot-panel-title">
                            {{ activeHotspot.title }}
                        </p>

                        <div class="presentation-player__panel-list">
                            <div
                                v-for="(panel, index) in panels"
                                :key="index"
                                class="presentation-player__panel-tab"
                                :class="{ 'presentation-player__panel-tab--active': activePanelIndex === index }"
                                @click="setActivePanel(index)"
                            >
                                <span class="presentation-player__panel-tab-title">
                                    {{ panel.title }}
                                </span>
                                <transition
                                    @before-enter="accordionBeforeEnter"
                                    @enter="accordionEnter"
                                    @leave="accordionLeave"
                                >
                                    <div
                                        v-if="activePanelIndex === index && panel.text"
                                        class="presentation-player__panel-tab-text"
                                        v-html="panel.text"
                                    />
                                </transition>
                            </div>
                        </div>

                        <button
                            class="presentation-player__continue button button--variant-default button--style-solid"
                            @click="closeHotspot"
                        >
                            <span class="button__label">{{ labels.continue }}</span>
                        </button>
                    </div>

                </div> <!-- /__hotspot-panel-inner -->
            </div>
        </transition>

        <!-- Image lightbox -->
        <Teleport to="body">
            <transition name="presentation-fade">
                <div
                    v-if="expandedMedia && activePanel?.mediaHtml"
                    class="presentation-lightbox"
                    @click.self="expandedMedia = false"
                >
                    <button
                        class="presentation-lightbox__close"
                        :aria-label="labels.close"
                        @click="expandedMedia = false"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                    <div
                        class="presentation-lightbox__media"
                        v-html="activePanel.mediaHtml"
                    />
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, computed, useSlots, onMounted, onBeforeUnmount } from "vue";

const slots = useSlots();

const root          = ref(null);
const mediaContainer = ref(null);
const video         = ref(null);
const hotspots      = ref([]);
const activeHotspot = ref(null);
const activePanelIndex = ref(0);
const panelCollapsed   = ref(false);
const expandedMedia    = ref(false);
const showIntro        = ref(true);

const isPlaying      = ref(false);
const autoplay       = ref(false);
const hotspotEnabled = ref(true);
const labels         = ref({});
const chaptersOpen   = ref(false);
const infoOpen       = ref(false);
const settingsOpen   = ref(false);
const hasVideo       = ref(false);

const currentTime = ref(0);

const panels      = computed(() => activeHotspot.value?.panels ?? []);
const activePanel = computed(() => panels.value[activePanelIndex.value] ?? null);

// Hotspots are sorted by timing in mounted(), so index order = chapter order.
// While a chapter is open we use its own index (robust against duplicate timings);
// during playback we fall back to the last chapter whose timing has passed.
const currentChapterIndex = computed(() => {
    if (activeHotspot.value) return hotspots.value.indexOf(activeHotspot.value);
    let current = -1;
    hotspots.value.forEach((h, i) => {
        if (h.timing <= currentTime.value) current = i;
    });
    return current;
});

const hasPrevChapter = computed(() => currentChapterIndex.value > 0);
const hasNextChapter = computed(() => currentChapterIndex.value < hotspots.value.length - 1);

function goToPrevChapter() {
    if (hasPrevChapter.value) jumpToHotspot(hotspots.value[currentChapterIndex.value - 1]);
}

// With a chapter panel open this mirrors the slideout "Weiter": resume playback
// so the video plays on to the next chapter (and pauses there). With no panel
// open it jumps straight to the next chapter, so the button always advances.
function goToNextChapter() {
    if (!hasNextChapter.value) return;
    if (activeHotspot.value) closeHotspot();
    else jumpToHotspot(hotspots.value[currentChapterIndex.value + 1]);
}

function toggleChapters() {
    chaptersOpen.value = !chaptersOpen.value;
    if (chaptersOpen.value) { infoOpen.value = false; settingsOpen.value = false; }
}

function toggleInfo() {
    infoOpen.value = !infoOpen.value;
    if (infoOpen.value) { chaptersOpen.value = false; settingsOpen.value = false; }
}

function toggleSettings() {
    settingsOpen.value = !settingsOpen.value;
    if (settingsOpen.value) { chaptersOpen.value = false; infoOpen.value = false; }
}

function startPresentation() {
    showIntro.value = false;
    video.value?.play();
}

function togglePlay() {
    if (!video.value) return;
    if (video.value.paused) {
        video.value.play();
    } else {
        video.value.pause();
    }
}

function onPlay()  { isPlaying.value = true;  }
function onPause() { isPlaying.value = false; }

function handleEnded() {
    isPlaying.value = false;
    if (autoplay.value) {
        hotspots.value.forEach((h) => { h.shown = false; });
        video.value.currentTime = 0;
        video.value.play();
    }
}

function handleTimeUpdate() {
    currentTime.value = video.value.currentTime;

    if (activeHotspot.value || !hotspotEnabled.value) return;

    const triggered = hotspots.value.find((h) => !h.shown && currentTime.value >= h.timing);
    if (triggered) activateHotspot(triggered);
}

function jumpToHotspot(hotspot) {
    hotspots.value.forEach((h) => {
        if (h !== hotspot) h.shown = h.timing <= hotspot.timing;
    });
    hotspot.shown = false;
    if (video.value) video.value.currentTime = hotspot.timing;
    activateHotspot(hotspot);
}

function activateHotspot(hotspot) {
    hotspot.shown = true;
    video.value?.pause();
    activeHotspot.value    = hotspot;
    activePanelIndex.value = 0;
    panelCollapsed.value   = false;
    expandedMedia.value    = false;
    chaptersOpen.value     = false;
    infoOpen.value         = false;
    settingsOpen.value     = false;
}

function setActivePanel(index) {
    if (activePanelIndex.value === index) return;
    activePanelIndex.value = index;
    expandedMedia.value    = false;
}

function closeHotspot() {
    if (!activeHotspot.value) return;
    const t = video.value?.currentTime ?? 0;
    hotspots.value.forEach((h) => { if (h.timing <= t) h.shown = true; });
    activeHotspot.value    = null;
    activePanelIndex.value = 0;
    panelCollapsed.value   = false;
    expandedMedia.value    = false;
    video.value?.play();
}

function accordionBeforeEnter(el) {
    el.style.height = '0';
    el.style.opacity = '0';
    el.style.overflow = 'hidden';
}

function accordionEnter(el, done) {
    const height = el.scrollHeight;
    el.style.transition = 'height 0.3s ease, opacity 0.3s ease';
    requestAnimationFrame(() => {
        el.style.height = height + 'px';
        el.style.opacity = '1';
    });
    el.addEventListener('transitionend', () => {
        el.style.height = 'auto';
        el.style.overflow = '';
        done();
    }, { once: true });
}

function accordionLeave(el, done) {
    el.style.height = el.scrollHeight + 'px';
    el.style.overflow = 'hidden';
    requestAnimationFrame(() => {
        el.style.transition = 'height 0.3s ease, opacity 0.3s ease';
        el.style.height = '0';
        el.style.opacity = '0';
    });
    el.addEventListener('transitionend', done, { once: true });
}

function onKeydown(e) {
    if (e.key !== 'Escape') return;
    if (expandedMedia.value) {
        expandedMedia.value = false;
        return;
    }
    chaptersOpen.value = false;
    infoOpen.value     = false;
    settingsOpen.value = false;
}

onMounted(() => {
    window.addEventListener('keydown', onKeydown);
    // ── Video: the one unavoidable DOM query ─────────────────────────────────
    video.value = root.value.querySelector("video");
    hasVideo.value = !!video.value;

    if (video.value) {
        video.value.removeAttribute("controls");
        video.value.addEventListener("timeupdate", handleTimeUpdate);
        video.value.addEventListener("play",       onPlay);
        video.value.addEventListener("pause",      onPause);
        video.value.addEventListener("ended",      handleEnded);
    }

    // ── Hotspot data from JSON prop, images from #media slot ref ─────────────
    labels.value = JSON.parse(root.value.dataset.labels ?? "{}");

    const raw = JSON.parse(root.value.dataset.hotspots ?? "[]");
    const mc  = mediaContainer.value;

    hotspots.value = raw.map((h, hi) => ({
        ...h,
        timing:      Number(h.timing) || 0,
        shown:       false,
        panels: h.panels.map((p, pi) => ({
            ...p,
            mediaHtml: mc?.querySelector(`[data-hotspot="${hi}"] [data-panel="${pi}"]`)?.innerHTML ?? "",
        })),
    })).sort((a, b) => a.timing - b.timing);
});

onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown);

    if (video.value) {
        video.value.removeEventListener("timeupdate", handleTimeUpdate);
        video.value.removeEventListener("play",       onPlay);
        video.value.removeEventListener("pause",      onPause);
        video.value.removeEventListener("ended",      handleEnded);
    }
});
</script>
